/**
 * @fileoverview User initialization service for MMR integration
 *
 * Handles automatic initialization of user MMR profiles when users sign in.
 * Integrates with Firebase Auth and UserDataService to ensure every user has a complete profile.
 */

import { User } from 'firebase/auth';
import { MMRService } from './MMRService';
import { UserProfile } from './types';
import { UserDataService } from '../firebase/userDataService';

export class UserInitializationService {
  private static initializationPromises = new Map<string, Promise<UserProfile>>();

  /**
   * Ensure user has a complete MMR profile
   * Prevents duplicate initialization calls for the same user
   */
  static async ensureUserProfile(user: User): Promise<UserProfile> {
    // Check if initialization is already in progress
    const existingPromise = this.initializationPromises.get(user.uid);
    if (existingPromise) {
      return existingPromise;
    }

    // Create and cache the initialization promise
    const initPromise = this.performUserInitialization(user);
    this.initializationPromises.set(user.uid, initPromise);

    try {
      const profile = await initPromise;
      // Clean up the cached promise on success
      this.initializationPromises.delete(user.uid);
      return profile;
    } catch (error) {
      // Clean up the cached promise on error
      this.initializationPromises.delete(user.uid);
      throw error;
    }
  }

  /**
   * Perform the actual user initialization
   */
  private static async performUserInitialization(user: User): Promise<UserProfile> {

    try {
      // CRITICAL: Check UserDataService first (new Firebase-first approach)
      const userDataServiceProfile = await UserDataService.getUserData(user.uid);

      if (userDataServiceProfile) {
        return userDataServiceProfile.profile;
      }

      // Fallback: Check legacy MMRService
      const existingProfile = await MMRService.getUserProfile(user.uid);

      if (existingProfile) {

        // Migrate legacy profile to new UserDataService
        const deviceId = 'migrated-' + Date.now(); // Temporary device ID for migration
        await UserDataService.createAnonymousUserData(user.uid, existingProfile, deviceId);

        // Upgrade to authenticated account if not anonymous
        if (!user.isAnonymous && user.email) {
          await UserDataService.upgradeToAuthenticatedAccount(user.uid, user.email);
        }

        return existingProfile;
      }

      // User needs initialization - create new profile
      const newProfile = await MMRService.initializeUserMMR(user);

      // Store in UserDataService immediately
      const deviceId = user.isAnonymous ? ('anonymous-' + Date.now()) : ('auth-' + Date.now());
      await UserDataService.createAnonymousUserData(user.uid, newProfile, deviceId);

      // Upgrade to authenticated if not anonymous
      if (!user.isAnonymous && user.email) {
        await UserDataService.upgradeToAuthenticatedAccount(user.uid, user.email);
      }

      return newProfile;

    } catch (error) {
      throw new Error(`User profile initialization failed: ${error}`);
    }
  }

  /**
   * Get user profile with automatic initialization fallback
   */
  static async getUserProfileWithInit(user: User): Promise<UserProfile> {
    try {
      return await this.ensureUserProfile(user);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if user is in placement phase
   */
  static async isUserInPlacement(user: User): Promise<boolean> {
    const profile = await this.getUserProfileWithInit(user);
    return !profile.mmr.isPlaced;
  }

  /**
   * Get user's current visible rating (0 if in placement)
   */
  static async getUserDisplayRating(user: User): Promise<number> {
    const profile = await this.getUserProfileWithInit(user);
    return profile.mmr.isPlaced ? profile.mmr.displayRating : 0;
  }

  /**
   * Get user's placement progress
   */
  static async getPlacementProgress(user: User): Promise<{
    gamesPlayed: number;
    gamesRemaining: number;
    isComplete: boolean;
  }> {
    const profile = await this.getUserProfileWithInit(user);
    const gamesPlayed = profile.mmr.placementGames;
    const gamesRemaining = MMRService['PLACEMENT_GAMES'] - gamesPlayed;

    return {
      gamesPlayed,
      gamesRemaining: Math.max(0, gamesRemaining),
      isComplete: profile.mmr.isPlaced
    };
  }

  /**
   * Clear cached initialization promises (useful for testing)
   */
  static clearCache(): void {
    this.initializationPromises.clear();
  }
}

export default UserInitializationService;